import { z } from "zod";
export const InstrumentIdentifiersSchema = z.object({
    ticker: z.string(),
    securityFigi: z.string().optional(),
    compositeFigi: z.string().optional(),
    shareClassFigi: z.string().optional(),
    cik: z.string().optional(),
    isin: z.string().optional(),
    cusip: z.string().optional(),
});
const PercentSchema = z.number().min(0).max(1);
const ConfidenceSchema = z.number().min(0).max(1);
export const ExposureGeoShareSchema = z
    .object({
    geo: z.string().min(1),
    pct: PercentSchema,
    level: z
        .enum(["country", "subnational", "region", "superregion"])
        .optional(),
    confidence: ConfidenceSchema.optional(),
})
    .strict();
export const ExposureCriticalSiteSchema = z
    .object({
    name: z.string().min(1),
    geo: z.string().min(1),
    confidence: ConfidenceSchema.optional(),
})
    .strict();
export const ExposureSupplierSchema = z
    .object({
    name: z.string().min(1),
    entityId: z.string().min(1).optional(),
    component: z.string().min(1).optional(),
    share_of_input_pct: PercentSchema.optional(),
    jurisdiction: z.string().min(1).optional(),
    confidence: ConfidenceSchema.optional(),
    criticality: z.enum(["sole-source", "high", "medium", "low"]).optional(),
})
    .strict();
export const ExposureCustomerSegmentSchema = z
    .object({
    segment: z.string().min(1),
    segment_id: z.string().min(1).optional(),
    share_of_rev_pct: PercentSchema.optional(),
    growth_sensitivity: z.number().min(-5).max(5).optional(),
    pricing_power: z.number().min(-2).max(2).optional(),
    confidence: ConfidenceSchema.optional(),
    type: z.enum(["b2b", "b2c", "b2g"]).optional(),
    contract_tenor: z.string().min(1).optional(),
})
    .strict();
const TariffExposureSchema = z
    .object({
    jurisdiction: z.string().min(1).optional(),
    goods: z.string().min(1).optional(),
    pct_rev_at_risk: PercentSchema.optional(),
    confidence: ConfidenceSchema.optional(),
})
    .strict();
const GovernmentContractSchema = z
    .object({
    agency: z.string().min(1).optional(),
    share_of_rev_pct: PercentSchema.optional(),
    contract_tenor: z.string().min(1).optional(),
    confidence: ConfidenceSchema.optional(),
})
    .strict();
export const ExposurePoliticalSchema = z
    .object({
    tariff_exposure: z.array(TariffExposureSchema).optional(),
    gov_contracts: z.array(GovernmentContractSchema).optional(),
    sanctions_jurisdictions: z.array(z.string().min(1)).optional(),
})
    .strict();
const MnaHistorySchema = z
    .object({
    target: z.string().min(1),
    year: z.number().int().optional(),
    value_usd: z.number().nonnegative().optional(),
    type: z.enum(["asset", "stock", "merger"]).optional(),
    rationale: z.string().min(1).optional(),
    status: z.enum(["announced", "closed", "integrated"]).optional(),
})
    .strict();
const MnaScenarioSchema = z
    .object({
    theme: z.string().min(1),
    description: z.string().min(1).optional(),
    likelihood: PercentSchema.optional(),
})
    .strict();
const MnaDefensiveSchema = z
    .object({
    is_target: z.boolean().optional(),
    rationale: z.string().min(1).optional(),
})
    .strict();
const MnaFrameworkSchema = z
    .object({
    capability_gaps: z.array(z.string().min(1)).optional(),
    capacity_usd: z.number().nonnegative().optional(),
    scenarios: z.array(MnaScenarioSchema).optional(),
    defensive: MnaDefensiveSchema.optional(),
})
    .strict();
export const ExposureMnaSchema = z
    .object({
    history: z.array(MnaHistorySchema).optional(),
    framework: MnaFrameworkSchema.optional(),
})
    .strict();
const LogisticsRouteSchema = z
    .object({
    origin: z.string().min(1),
    destination: z.string().min(1),
    mode: z.enum(["ocean", "air", "rail", "road"]).optional(),
    importance: z.number().min(0).max(1).optional(),
})
    .strict();
const LogisticsProviderSchema = z
    .object({
    name: z.string().min(1),
    role: z
        .enum(["carrier", "3pl", "warehouse", "port", "freight_forwarder"])
        .optional(),
    share_of_logistics_pct: PercentSchema.optional(),
})
    .strict();
const LogisticsChannelSchema = z
    .object({
    name: z.string().min(1),
    owned: z.boolean().optional(),
})
    .strict();
export const ExposureLogisticsSchema = z
    .object({
    routes: z.array(LogisticsRouteSchema).optional(),
    providers: z.array(LogisticsProviderSchema).optional(),
    channels: z.array(LogisticsChannelSchema).optional(),
})
    .strict();
const BusinessSegmentSchema = z
    .object({
    name: z.string().min(1),
    share_of_rev_pct: PercentSchema.optional(),
    margin_pct: PercentSchema.optional(),
})
    .strict();
export const ExposureMarketTrendsSchema = z
    .object({
    peer_tickers: z.array(z.string().min(1)).optional(),
    analyst_coverage: z.enum(["low", "medium", "high"]).optional(),
    business_segments: z.array(BusinessSegmentSchema).optional(),
})
    .strict();
const ThemeSchema = z
    .object({
    name: z.string().min(1),
    description: z.string().min(1).optional(),
    investment_level: z.enum(["low", "medium", "high"]).optional(),
    timeframe: z.string().min(1).optional(),
})
    .strict();
export const ExposureCommoditySchema = z
    .object({
    commodity: z.string().min(1),
    cost_share_pct: PercentSchema.optional(),
    price_beta_to_margin: z.number().min(-5).max(5).optional(),
    confidence: ConfidenceSchema.optional(),
})
    .strict();
export const ExposureFinancingSchema = z
    .object({
    fixed_pct: PercentSchema.optional(),
    variable_pct: PercentSchema.optional(),
    avg_cost_bps: z.number().min(0).optional(),
    duration: z.string().min(1).optional(),
    currency: z.string().min(1).optional(),
    hedge_ratio: PercentSchema.optional(),
    interest_reset_frequency: z
        .enum(["monthly", "quarterly", "semiannual", "annual"])
        .optional(),
    confidence: ConfidenceSchema.optional(),
})
    .strict();
export const ExposurePolicySchema = z
    .object({
    // New canonical reference
    policy_code: z.string().min(1).optional(),
    jurisdictions: z.array(z.string().min(1)).optional(),
    // Back-compat (to be deprecated)
    policy: z.string().min(1).optional(),
    exposure_strength: PercentSchema,
    scope: z.array(z.string().min(1)).optional(),
    confidence: ConfidenceSchema.optional(),
    expiry: z.string().datetime().optional(),
})
    .strict()
    .refine((v) => Boolean(v.policy_code || v.policy), {
    message: "policy_code (preferred) or policy is required",
});
export const ExposureRegistryEntrySchema = z
    .object({
    instrumentId: z.string().min(1),
    version: z.string().min(1),
    schemaVersion: z.string().min(1),
    updatedAt: z.string().datetime().optional(),
    updatedBy: z.string().min(1).optional(),
    expiry: z.string().datetime().optional(),
    sources: z.array(z.string().min(1)).min(1),
    confidence: ConfidenceSchema.optional(),
    geography: z
        .object({
        revenue_by_geo: z.array(ExposureGeoShareSchema).optional(),
        manufacturing_geos: z.array(z.string().min(1)).optional(),
        critical_sites: z.array(ExposureCriticalSiteSchema).optional(),
    })
        .passthrough()
        .optional(),
    suppliers: z.array(ExposureSupplierSchema).optional(),
    customer_segments: z.array(ExposureCustomerSegmentSchema).optional(),
    commodities: z.array(ExposureCommoditySchema).optional(),
    financing: ExposureFinancingSchema.optional(),
    policy: z.array(ExposurePolicySchema).optional(),
    political: ExposurePoliticalSchema.optional(),
    mna: ExposureMnaSchema.optional(),
    logistics: ExposureLogisticsSchema.optional(),
    market_trends: ExposureMarketTrendsSchema.optional(),
    themes: z.array(ThemeSchema).optional(),
})
    .passthrough();
export const CanonicalEventTierSchema = z.enum(["slow", "fast"]);
export const CanonicalEventSourceAuthoritySchema = z.enum([
    "high",
    "medium",
    "low",
]);
export const KnownCanonicalEventTypeSchema = z.enum([
    "NewsEvent",
    "SentimentEvent",
    "ShipmentEvent",
    "TransactionEvent",
    "PolicyEvent",
    "RatesEvent",
    "WeatherEvent",
    "EarningsEvent",
    "FilingEvent",
]);
export const CanonicalEventEntitySchema = z
    .object({
    type: z.string().min(1),
    id: z.string().min(1),
})
    .strict();
export const CanonicalEventConfidenceSchema = z
    .object({
    signal: ConfidenceSchema,
})
    .passthrough();
export const CanonicalEventSchema = z
    .object({
    eventId: z.string().min(1),
    ts: z.string().datetime(),
    type: z.union([KnownCanonicalEventTypeSchema, z.string().min(1)]),
    source: z.string().min(1),
    tier: CanonicalEventTierSchema,
    ttlHours: z.number().min(0).optional(),
    dedupe_key: z.string().min(1).optional(),
    importance: z.number().min(0).max(1).optional(),
    novelty: z.number().min(0).max(1).optional(),
    clusterId: z.string().min(1).optional(),
    source_authority: CanonicalEventSourceAuthoritySchema.optional(),
    jurisdictions: z.array(z.string().min(1)).optional(),
    entities: z.array(CanonicalEventEntitySchema).min(1),
    tags: z.array(z.string().min(1)).optional(),
    payload: z.record(z.any()).optional(),
    confidence: CanonicalEventConfidenceSchema,
    schemaVersion: z.string().min(1),
})
    .passthrough();
export const KnownImpactMetricSchema = z.enum([
    "gross_margin_bps",
    "revenue_growth_pct",
    "eps_pct",
    "op_margin_bps",
]);
export const ImpactUnitsSchema = z.enum(["bps", "pct"]);
export const ImpactLagSchema = z.enum(["t+1w", "t+1m", "t+1q"]);
export const ImpactExplanationEventStepSchema = z
    .object({
    event: z.string().min(1).optional(),
    note: z.string().min(1).optional(),
})
    .refine((value) => Boolean(value.event || value.note), {
    message: "event or note is required",
});
export const ImpactExplanationEdgeStepSchema = z
    .object({
    edge: z.string().min(1).optional(),
    weight: z.number().optional(),
    elasticity: z.number().optional(),
})
    .refine((value) => Boolean(value.edge), {
    message: "edge is required when describing an exposure step",
});
export const ImpactBundleSchema = z
    .object({
    instrumentId: z.string().min(1),
    affected_metric: z.union([KnownImpactMetricSchema, z.string().min(1)]),
    magnitude: z.number(),
    units: z.union([ImpactUnitsSchema, z.string().min(1)]),
    lag: z.union([ImpactLagSchema, z.string().min(1)]),
    confidence: ConfidenceSchema,
    direction: z.enum(["positive", "negative"]).optional(),
    magnitude_ci: z.object({ low: z.number(), high: z.number() }).optional(),
    path_signature: z.string().min(1).optional(),
    assumptions: z.array(z.string().min(1)).optional(),
    explanation_path: z
        .array(z.union([
        ImpactExplanationEventStepSchema,
        ImpactExplanationEdgeStepSchema,
    ]))
        .min(1),
    provenance: z
        .array(z
        .object({
        artifactId: z.string().min(1).optional(),
        url: z.string().url().optional(),
        title: z.string().min(1).optional(),
    })
        .strict())
        .optional(),
    evidence: z
        .object({
        events: z.number().min(0),
        unique_domains: z.number().min(0).optional(),
        slow_events: z.number().min(0).optional(),
    })
        .optional(),
    anchored_by_bedrock: z.boolean().optional(),
})
    .passthrough();
export const ImpactBundleTrailSchema = z
    .object({
    instrumentId: z.string().min(1),
    metric: z.union([KnownImpactMetricSchema, z.string().min(1)]),
    asOf: z.string().min(1),
    eventId: z.string().min(1),
    tier: CanonicalEventTierSchema,
    source: z.string().min(1),
    bundle: ImpactBundleSchema,
    routing_keys: z.array(z.string().min(1)).optional(),
    createdAt: z.string().datetime().optional(),
})
    .strict();
// Qualitative second-order impact bundle (non-numeric)
export const QualitativeImpactBundleSchema = z
    .object({
    instrumentId: z.string().min(1),
    theme: z.string().min(1),
    direction: z.enum(["positive", "negative", "mixed"]),
    strength: z.enum(["low", "moderate", "high"]),
    lag: z.union([ImpactLagSchema, z.string().min(1)]),
    scope: z.array(z.string().min(1)).optional(),
    mechanisms: z
        .array(z.object({
        id: z.string().min(1),
        direction: z.enum(["positive", "negative"]),
        note: z.string().min(1).optional(),
    }))
        .min(1),
    anchored_by_bedrock: z.boolean(),
    anchors: z.array(z.object({ edge: z.string().min(1), conf: z.number().optional() })),
    evidence: z
        .object({
        events: z.number().min(0),
        unique_domains: z.number().min(0).optional(),
        slow_events: z.number().min(0).optional(),
    })
        .optional(),
    explanation_path: z
        .array(z.union([
        ImpactExplanationEventStepSchema,
        ImpactExplanationEdgeStepSchema,
    ]))
        .min(1),
    provenance: z
        .array(z
        .object({
        artifactId: z.string().min(1).optional(),
        url: z.string().url().optional(),
        title: z.string().min(1).optional(),
    })
        .strict())
        .optional(),
    confidence: z.number().min(0).max(1).optional(),
    assumptions: z.array(z.string().min(1)).optional(),
    schemaVersion: z.string().min(1).optional(),
})
    .passthrough();
// Agent-authored storyline object
// Mechanism candidate (experimental lane)
export const MechanismCandidateSchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    proposedRequires: z.array(z.string().min(1)).optional(),
    anchors: z
        .array(z.object({ edge: z.string().min(1), conf: z.number().optional() }))
        .min(1),
    explanationPath: z
        .array(z.object({
        event: z.string().optional(),
        edge: z.string().optional(),
        note: z.string().optional(),
    }))
        .min(1),
    evidence: z
        .object({
        events: z.number().min(0),
        uniqueDomains: z.number().min(0).optional(),
        slowEvents: z.number().min(0).optional(),
        instruments: z.array(z.string().min(1)).optional(),
    })
        .strict(),
    confidence: z.number().min(0).max(1).optional(),
    directionStability: z.number().min(0).max(1).optional(),
    clusterId: z.string().min(1).optional(),
    lineage: z.array(z.string().min(1)).min(1),
    createdBy: z.enum(["model", "steward"]).default("model"),
    state: z
        .enum([
        "experimental",
        "provisional",
        "validated",
        "canonical",
        "deprecated",
    ])
        .default("experimental"),
    stewardNotes: z.array(z.string().min(1)).optional(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1).optional(),
    schemaVersion: z.string().min(1).default("mechCand.v0.1"),
})
    .strict();
export const MechanismRegistryItemSchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    requires: z.array(z.string().min(1)).optional(),
    defaultLag: z.string().min(1).optional(),
    reliability: z.enum(["high", "med", "low"]).optional(),
    state: z
        .enum(["provisional", "validated", "canonical", "deprecated"])
        .default("provisional"),
    version: z.string().min(1),
    sources: z.array(z.string().min(1)).default([]),
    audit: z
        .array(z.object({
        action: z.enum(["approve", "suppress", "revise", "graduate"]),
        by: z.string().min(1),
        at: z.string().min(1),
        notes: z.string().optional(),
    }))
        .default([]),
    linkedCandidateIds: z.array(z.string().min(1)).optional(),
    schemaVersion: z.string().min(1).default("mechReg.v0.1"),
})
    .strict();
const BucketInferenceFocusAreaSchema = z
    .object({
    title: z.string().min(1),
    rationale: z.string().min(1),
    quantMetric: z.string().min(1).optional(),
})
    .strict();
const BucketGapAnalysisSchema = z
    .object({
    summary: z.string().min(1).optional(),
    focusAreas: z.array(BucketInferenceFocusAreaSchema).min(1),
    followUps: z.array(z.string().min(1)).optional(),
})
    .strict();
const BucketInferenceImpactSchema = z
    .object({
    title: z.string().min(1),
    approach: z.string().min(1),
    estimate: z.string().min(1).optional(),
})
    .strict();
const BucketQuantAnalysisSchema = z
    .object({
    impacts: z.array(BucketInferenceImpactSchema).min(1),
    caveats: z.array(z.string().min(1)).optional(),
    followUps: z.array(z.string().min(1)).optional(),
})
    .strict();
const MechanismDescriptorSchema = z
    .object({
    family: z.string().trim().min(1),
    subtype: z.string().trim().min(1).optional(),
    orderOfImpact: z.enum(["first", "second", "third"]).optional(),
    direction: z.enum(["positive", "negative", "mixed"]).optional(),
    bridgeEntities: z.array(z.string().trim().min(1)).optional(),
})
    .strict();
const BucketInferenceSchema = z
    .object({
    bucket: z.string().min(1),
    source: z.string().min(1),
    generatedAt: z.string().min(1),
    gapAnalysis: BucketGapAnalysisSchema.optional(),
    quantAnalysis: BucketQuantAnalysisSchema.optional(),
})
    .strict();
export const StorylineSchema = z
    .object({
    id: z.string().min(1),
    title: z.string().min(1),
    // Optional executive summary derived from claims/mechanisms
    summary: z.string().min(1).optional(),
    trigger_event: z.object({
        eventId: z.string().min(1),
        ts: z.string().min(1),
        tier: z.enum(["fast", "slow"]),
    }),
    hops: z.number().min(1),
    mechanisms: z.array(z.object({
        id: z.string().min(1),
        name: z.string().min(1),
        reliability: z.enum(["high", "med", "low"]),
    })),
    claims: z.array(z.object({
        text: z.string().min(1),
        anchors: z
            .array(z.object({ edge: z.string().min(1), conf: z.number().optional() }))
            .min(0),
        citations: z
            .array(z.object({
            type: z.enum([
                "10-K",
                "10-Q",
                "8-K",
                "call",
                "rule",
                "news",
                "public-data",
            ]),
            ref: z.string().min(1),
        }))
            .min(1),
        status: z.enum(["anchored", "watch"]),
    })),
    impacts: z
        .array(z.union([ImpactBundleSchema, QualitativeImpactBundleSchema]))
        .min(1),
    // Report-like prescriptive framing (optional)
    decision: z
        .object({
        picked: z.enum(["positive", "neutral", "negative"]),
        rationale: z.string().min(1),
    })
        .optional(),
    implications: z
        .object({
        stock: z.string().min(1).nullable(),
        timeframe: z.enum(["immediate", "short", "medium", "long"]).nullable(),
    })
        .nullable()
        .optional(),
    historicalEvidence: z
        .array(z.object({
        example: z.string().min(1),
        // keep nullable to satisfy strict schema while allowing absence
        period: z.string().nullable(),
        description: z.string().min(1),
        relevance: z.string().min(1),
    }))
        .optional(),
    // Overall narrative confidence derived from anchors/mechanisms
    confidence: z.number().min(0).max(1).optional(),
    next_actions: z
        .array(z.object({
        action: z.string().min(1),
        params: z.record(z.unknown()).optional(),
    }))
        .optional(),
    what_would_change: z.array(z.string().min(1)).optional(),
    asOf: z.string().min(1),
    path_signature: z.string().min(1).optional(),
    schemaVersion: z.string().min(1).default("story.v0.2"),
    maturity: z
        .enum(["experimental", "provisional", "validated", "canonical"])
        .optional(),
    bucketInference: BucketInferenceSchema.optional(),
    mechanism: MechanismDescriptorSchema.optional(),
})
    .strict();
// Second-order domain summary (shared contract)
export const SecondOrderDomainKeySchema = z.enum([
    "marketTrends",
    "politics",
    "regulatory",
    "mna",
    "logistics",
    "customers",
    "suppliers",
    "themes",
]);
// Normalized feed item for dashboard rendering
export const IntelligenceFeedItemSchema = z
    .object({
    id: z.string().min(1),
    section: SecondOrderDomainKeySchema,
    instrumentIds: z.array(z.string().min(1)).min(1),
    title: z.string().min(1),
    summary: z.string().min(1),
    ts: z.string().min(1),
    chips: z.array(z.enum([
        "Storyline",
        "Anchored",
        "2-hop",
        "3-hop",
        "Fast",
        "Slow",
        "Qualitative",
        "Numeric",
        "Experimental",
        "Provisional",
        "Validated",
        "Watch",
    ])),
    importance: z.number().min(0).max(1),
    // Optional top-level direction derived from storyline decision or
    // underlying impact, used by UI cards for green/red styling.
    direction: z.enum(["positive", "neutral", "negative"]).optional(),
    impact: z
        .union([ImpactBundleSchema, QualitativeImpactBundleSchema])
        .optional(),
    storylineId: z.string().min(1).optional(),
    evidence: z
        .object({
        events: z.number().min(0),
        unique_domains: z.number().min(0).optional(),
        slow_events: z.number().min(0).optional(),
    })
        .optional(),
    sources: z
        .array(z.object({
        label: z.string().min(1),
        url: z.string().url().optional(),
    }))
        .optional(),
    tags: z.array(z.string().min(1)).optional(),
    dedupeKey: z.string().min(1).optional(),
    schemaVersion: z.string().min(1).default("feed.v0.1"),
})
    .strict();
export const SecondOrderSnapshotSchema = z
    .object({
    instrumentId: z.string().min(1),
    asOf: z.string().min(1).optional().nullable(),
    etag: z.string().min(1),
    anchoredImpacts: z.array(ImpactBundleTrailSchema),
    signalOnlyImpacts: z.array(ImpactBundleTrailSchema),
    counts: z.object({
        anchored: z.number().min(0),
        signalOnly: z.number().min(0),
    }),
})
    .strict();
export const SecondOrderStateResponseSchema = z
    .object({
    success: z.literal(true),
    instrumentId: z.string().min(1),
    snapshot: SecondOrderSnapshotSchema,
    trails: z.array(ImpactBundleTrailSchema),
})
    .strict();
export const CustomerSegmentResearchSchema = z.object({
    segments: z.array(z.object({
        name: z.string(),
        description: z.string(),
        percentage: z.number().optional(),
        trends: z.array(z.string()).optional(),
        risks: z.array(z.string()).optional(),
    })),
    summary: z.string().optional(),
});
export const LegislationResearchSchema = z.object({
    bills: z.array(z.object({
        name: z.string(),
        code: z.string().optional(),
        status: z.string(),
        impact: z.string(),
        summary: z.string(),
        likelihood: z.string().optional(),
    })),
    summary: z.string().optional(),
});
export const SupplyChainResearchSchema = z.object({
    suppliers: z.array(z.object({
        name: z.string(),
        component: z.string().optional(),
        risk: z.string().optional(),
        location: z.string().optional(),
        criticality: z.enum(["high", "medium", "low"]).optional(),
        ticker: z.string().optional(),
    })),
    risks: z.array(z.string()).optional(),
    logistics: z.array(z.string()).optional(),
    summary: z.string().optional(),
});
const PercentJsonSchema = Object.freeze({
    type: "number",
    minimum: 0,
    maximum: 1,
});
const NonEmptyStringJsonSchema = Object.freeze({
    type: "string",
    minLength: 1,
});
const ExposureGeoShareJsonSchema = Object.freeze({
    type: "object",
    properties: {
        geo: NonEmptyStringJsonSchema,
        pct: PercentJsonSchema,
        level: {
            type: "string",
            enum: ["country", "subnational", "region", "superregion"],
        },
        confidence: PercentJsonSchema,
    },
    required: ["geo", "pct"],
    additionalProperties: false,
});
const ExposureCriticalSiteJsonSchema = Object.freeze({
    type: "object",
    properties: {
        name: NonEmptyStringJsonSchema,
        geo: NonEmptyStringJsonSchema,
        confidence: PercentJsonSchema,
    },
    required: ["name", "geo"],
    additionalProperties: false,
});
const ExposureSupplierJsonSchema = Object.freeze({
    type: "object",
    properties: {
        name: NonEmptyStringJsonSchema,
        entityId: NonEmptyStringJsonSchema,
        component: NonEmptyStringJsonSchema,
        share_of_input_pct: PercentJsonSchema,
        jurisdiction: NonEmptyStringJsonSchema,
        confidence: PercentJsonSchema,
        criticality: {
            type: "string",
            enum: ["sole-source", "high", "medium", "low"],
        },
    },
    required: ["name"],
    additionalProperties: false,
});
const ExposureCustomerSegmentJsonSchema = Object.freeze({
    type: "object",
    properties: {
        segment: NonEmptyStringJsonSchema,
        segment_id: NonEmptyStringJsonSchema,
        share_of_rev_pct: PercentJsonSchema,
        growth_sensitivity: {
            type: "number",
            minimum: -5,
            maximum: 5,
        },
        pricing_power: { type: "number", minimum: -2, maximum: 2 },
        confidence: PercentJsonSchema,
        type: { type: "string", enum: ["b2b", "b2c", "b2g"] },
        contract_tenor: NonEmptyStringJsonSchema,
    },
    required: ["segment"],
    additionalProperties: false,
});
const ExposureCommodityJsonSchema = Object.freeze({
    type: "object",
    properties: {
        commodity: NonEmptyStringJsonSchema,
        cost_share_pct: PercentJsonSchema,
        price_beta_to_margin: {
            type: "number",
            minimum: -5,
            maximum: 5,
        },
        confidence: PercentJsonSchema,
    },
    required: ["commodity"],
    additionalProperties: false,
});
const ExposureFinancingJsonSchema = Object.freeze({
    type: "object",
    properties: {
        fixed_pct: PercentJsonSchema,
        variable_pct: PercentJsonSchema,
        avg_cost_bps: {
            type: "number",
            minimum: 0,
        },
        duration: NonEmptyStringJsonSchema,
        currency: NonEmptyStringJsonSchema,
        hedge_ratio: PercentJsonSchema,
        interest_reset_frequency: {
            type: "string",
            enum: ["monthly", "quarterly", "semiannual", "annual"],
        },
        confidence: PercentJsonSchema,
    },
    additionalProperties: false,
});
const ExposurePolicyJsonSchema = Object.freeze({
    type: "object",
    properties: {
        policy_code: NonEmptyStringJsonSchema,
        jurisdictions: { type: "array", items: NonEmptyStringJsonSchema },
        policy: NonEmptyStringJsonSchema,
        exposure_strength: PercentJsonSchema,
        scope: {
            type: "array",
            items: NonEmptyStringJsonSchema,
        },
        confidence: PercentJsonSchema,
        expiry: { type: "string", format: "date-time" },
    },
    anyOf: [
        { required: ["policy_code", "exposure_strength"] },
        { required: ["policy", "exposure_strength"] },
    ],
    additionalProperties: false,
});
const TariffExposureJsonSchema = Object.freeze({
    type: "object",
    properties: {
        jurisdiction: NonEmptyStringJsonSchema,
        goods: NonEmptyStringJsonSchema,
        pct_rev_at_risk: PercentJsonSchema,
        confidence: PercentJsonSchema,
    },
    additionalProperties: false,
});
const GovernmentContractJsonSchema = Object.freeze({
    type: "object",
    properties: {
        agency: NonEmptyStringJsonSchema,
        share_of_rev_pct: PercentJsonSchema,
        contract_tenor: NonEmptyStringJsonSchema,
        confidence: PercentJsonSchema,
    },
    additionalProperties: false,
});
const ExposurePoliticalJsonSchema = Object.freeze({
    type: "object",
    properties: {
        tariff_exposure: { type: "array", items: TariffExposureJsonSchema },
        gov_contracts: { type: "array", items: GovernmentContractJsonSchema },
        sanctions_jurisdictions: {
            type: "array",
            items: NonEmptyStringJsonSchema,
        },
    },
    additionalProperties: false,
});
const MnaHistoryJsonSchema = Object.freeze({
    type: "object",
    properties: {
        target: NonEmptyStringJsonSchema,
        year: { type: "number", multipleOf: 1 },
        value_usd: { type: "number", minimum: 0 },
        type: { type: "string", enum: ["asset", "stock", "merger"] },
        rationale: NonEmptyStringJsonSchema,
        status: { type: "string", enum: ["announced", "closed", "integrated"] },
    },
    required: ["target"],
    additionalProperties: false,
});
const MnaScenarioJsonSchema = Object.freeze({
    type: "object",
    properties: {
        theme: NonEmptyStringJsonSchema,
        description: NonEmptyStringJsonSchema,
        likelihood: PercentJsonSchema,
    },
    required: ["theme"],
    additionalProperties: false,
});
const MnaDefensiveJsonSchema = Object.freeze({
    type: "object",
    properties: {
        is_target: { type: "boolean" },
        rationale: NonEmptyStringJsonSchema,
    },
    additionalProperties: false,
});
const MnaFrameworkJsonSchema = Object.freeze({
    type: "object",
    properties: {
        capability_gaps: { type: "array", items: NonEmptyStringJsonSchema },
        capacity_usd: { type: "number", minimum: 0 },
        scenarios: { type: "array", items: MnaScenarioJsonSchema },
        defensive: MnaDefensiveJsonSchema,
    },
    additionalProperties: false,
});
const ExposureMnaJsonSchema = Object.freeze({
    type: "object",
    properties: {
        history: { type: "array", items: MnaHistoryJsonSchema },
        framework: MnaFrameworkJsonSchema,
    },
    additionalProperties: false,
});
const LogisticsRouteJsonSchema = Object.freeze({
    type: "object",
    properties: {
        origin: NonEmptyStringJsonSchema,
        destination: NonEmptyStringJsonSchema,
        mode: { type: "string", enum: ["ocean", "air", "rail", "road"] },
        importance: PercentJsonSchema,
    },
    required: ["origin", "destination"],
    additionalProperties: false,
});
const LogisticsProviderJsonSchema = Object.freeze({
    type: "object",
    properties: {
        name: NonEmptyStringJsonSchema,
        role: {
            type: "string",
            enum: ["carrier", "3pl", "warehouse", "port", "freight_forwarder"],
        },
        share_of_logistics_pct: PercentJsonSchema,
    },
    required: ["name"],
    additionalProperties: false,
});
const LogisticsChannelJsonSchema = Object.freeze({
    type: "object",
    properties: {
        name: NonEmptyStringJsonSchema,
        owned: { type: "boolean" },
    },
    required: ["name"],
    additionalProperties: false,
});
const ExposureLogisticsJsonSchema = Object.freeze({
    type: "object",
    properties: {
        routes: { type: "array", items: LogisticsRouteJsonSchema },
        providers: { type: "array", items: LogisticsProviderJsonSchema },
        channels: { type: "array", items: LogisticsChannelJsonSchema },
    },
    additionalProperties: false,
});
const BusinessSegmentJsonSchema = Object.freeze({
    type: "object",
    properties: {
        name: NonEmptyStringJsonSchema,
        share_of_rev_pct: PercentJsonSchema,
        margin_pct: PercentJsonSchema,
    },
    required: ["name"],
    additionalProperties: false,
});
const ExposureMarketTrendsJsonSchema = Object.freeze({
    type: "object",
    properties: {
        peer_tickers: { type: "array", items: NonEmptyStringJsonSchema },
        analyst_coverage: { type: "string", enum: ["low", "medium", "high"] },
        business_segments: { type: "array", items: BusinessSegmentJsonSchema },
    },
    additionalProperties: false,
});
const ThemeJsonSchema = Object.freeze({
    type: "object",
    properties: {
        name: NonEmptyStringJsonSchema,
        description: NonEmptyStringJsonSchema,
        investment_level: { type: "string", enum: ["low", "medium", "high"] },
        timeframe: NonEmptyStringJsonSchema,
    },
    required: ["name"],
    additionalProperties: false,
});
export const ExposureRegistryEntryJsonSchema = Object.freeze({
    $id: "https://schemas.akem.ai/exposure-registry-entry.json",
    type: "object",
    properties: {
        instrumentId: NonEmptyStringJsonSchema,
        version: NonEmptyStringJsonSchema,
        schemaVersion: NonEmptyStringJsonSchema,
        updatedAt: {
            type: "string",
            format: "date-time",
        },
        updatedBy: NonEmptyStringJsonSchema,
        expiry: { type: "string", format: "date-time" },
        sources: {
            type: "array",
            items: NonEmptyStringJsonSchema,
            minItems: 1,
        },
        confidence: PercentJsonSchema,
        geography: {
            type: "object",
            properties: {
                revenue_by_geo: {
                    type: "array",
                    items: ExposureGeoShareJsonSchema,
                },
                manufacturing_geos: {
                    type: "array",
                    items: NonEmptyStringJsonSchema,
                },
                critical_sites: {
                    type: "array",
                    items: ExposureCriticalSiteJsonSchema,
                },
            },
            additionalProperties: true,
        },
        suppliers: {
            type: "array",
            items: ExposureSupplierJsonSchema,
        },
        customer_segments: {
            type: "array",
            items: ExposureCustomerSegmentJsonSchema,
        },
        commodities: {
            type: "array",
            items: ExposureCommodityJsonSchema,
        },
        financing: ExposureFinancingJsonSchema,
        policy: {
            type: "array",
            items: ExposurePolicyJsonSchema,
        },
        political: ExposurePoliticalJsonSchema,
        mna: ExposureMnaJsonSchema,
        logistics: ExposureLogisticsJsonSchema,
        market_trends: ExposureMarketTrendsJsonSchema,
        themes: { type: "array", items: ThemeJsonSchema },
    },
    required: ["instrumentId", "version", "schemaVersion", "sources"],
    additionalProperties: true,
});
const CanonicalEventEntityJsonSchema = Object.freeze({
    type: "object",
    properties: {
        type: NonEmptyStringJsonSchema,
        id: NonEmptyStringJsonSchema,
    },
    required: ["type", "id"],
    additionalProperties: false,
});
const CanonicalEventConfidenceJsonSchema = Object.freeze({
    type: "object",
    properties: {
        signal: PercentJsonSchema,
    },
    required: ["signal"],
    additionalProperties: true,
});
export const CanonicalEventJsonSchema = Object.freeze({
    $id: "https://schemas.akem.ai/canonical-event.json",
    type: "object",
    properties: {
        eventId: NonEmptyStringJsonSchema,
        ts: {
            type: "string",
            format: "date-time",
        },
        type: NonEmptyStringJsonSchema,
        source: NonEmptyStringJsonSchema,
        tier: {
            type: "string",
            enum: ["slow", "fast"],
        },
        ttlHours: {
            type: "number",
            minimum: 0,
        },
        dedupe_key: NonEmptyStringJsonSchema,
        importance: { type: "number", minimum: 0, maximum: 1 },
        novelty: { type: "number", minimum: 0, maximum: 1 },
        clusterId: NonEmptyStringJsonSchema,
        source_authority: {
            type: "string",
            enum: ["high", "medium", "low"],
        },
        jurisdictions: { type: "array", items: NonEmptyStringJsonSchema },
        entities: {
            type: "array",
            items: CanonicalEventEntityJsonSchema,
            minItems: 1,
        },
        tags: {
            type: "array",
            items: NonEmptyStringJsonSchema,
        },
        payload: {
            type: "object",
            additionalProperties: true,
        },
        confidence: CanonicalEventConfidenceJsonSchema,
        schemaVersion: NonEmptyStringJsonSchema,
    },
    required: [
        "eventId",
        "ts",
        "type",
        "source",
        "tier",
        "entities",
        "confidence",
        "schemaVersion",
    ],
    additionalProperties: true,
});
const ImpactExplanationEventStepJsonSchema = Object.freeze({
    type: "object",
    properties: {
        event: NonEmptyStringJsonSchema,
        note: NonEmptyStringJsonSchema,
    },
    additionalProperties: false,
    anyOf: [{ required: ["event"] }, { required: ["note"] }],
});
const ImpactExplanationEdgeStepJsonSchema = Object.freeze({
    type: "object",
    properties: {
        edge: NonEmptyStringJsonSchema,
        weight: {
            type: "number",
        },
        elasticity: {
            type: "number",
        },
    },
    additionalProperties: false,
    required: ["edge"],
});
export const ImpactBundleJsonSchema = Object.freeze({
    $id: "https://schemas.akem.ai/impact-bundle.json",
    type: "object",
    properties: {
        instrumentId: NonEmptyStringJsonSchema,
        affected_metric: NonEmptyStringJsonSchema,
        magnitude: {
            type: "number",
        },
        units: NonEmptyStringJsonSchema,
        lag: NonEmptyStringJsonSchema,
        confidence: PercentJsonSchema,
        direction: { type: "string", enum: ["positive", "negative"] },
        magnitude_ci: {
            type: "object",
            properties: { low: { type: "number" }, high: { type: "number" } },
            required: ["low", "high"],
            additionalProperties: false,
        },
        path_signature: NonEmptyStringJsonSchema,
        assumptions: { type: "array", items: NonEmptyStringJsonSchema },
        explanation_path: {
            type: "array",
            items: {
                anyOf: [
                    ImpactExplanationEventStepJsonSchema,
                    ImpactExplanationEdgeStepJsonSchema,
                ],
            },
            minItems: 1,
        },
        provenance: {
            type: "array",
            items: {
                type: "object",
                properties: {
                    artifactId: NonEmptyStringJsonSchema,
                    url: {
                        type: "string",
                        format: "uri",
                    },
                },
                additionalProperties: false,
            },
        },
        evidence: {
            type: "object",
            properties: {
                events: {
                    type: "number",
                    minimum: 0,
                },
                unique_domains: {
                    type: "number",
                    minimum: 0,
                },
                slow_events: {
                    type: "number",
                    minimum: 0,
                },
            },
            required: ["events"],
            additionalProperties: false,
        },
        anchored_by_bedrock: {
            type: "boolean",
        },
    },
    required: [
        "instrumentId",
        "affected_metric",
        "magnitude",
        "units",
        "lag",
        "confidence",
        "explanation_path",
    ],
    additionalProperties: true,
});
// Minimal cross-shared IO schemas for critical tools
export const ConnectWidgetsArgs = z.object({
    sourceWidgetId: z.string(),
    targetWidgetId: z.string(),
    // Preferred
    outputId: z.string().optional(),
    inputId: z.string().optional(),
    // Back-compat
    fromOutput: z.string().optional(),
    toInput: z.string().optional(),
});
export const ApplyCanvasPlanArgs = z.object({
    plan: z
        .object({
        widgets: z
            .array(z.object({
            type: z.string(),
            title: z.string().optional(),
            position: z.object({ x: z.number(), y: z.number() }).optional(),
            config: z.object({}).passthrough().optional(),
        }))
            .optional(),
        connections: z
            .array(z.object({
            fromIndex: z.number(),
            fromOutput: z.string(),
            toIndex: z.number(),
            toInput: z.string(),
        }))
            .optional(),
    })
        .optional(),
    dsl: z.string().optional(),
    showPlaceholders: z.boolean().optional(),
    groupId: z.string().optional(),
    canvas: z
        .object({
        key: z.string().min(1),
        kind: z.string().min(1),
        name: z.string().optional(),
    })
        .optional(),
});
export const UpdateWidgetInputsArgs = z.object({
    target: z
        .object({
        widgetId: z.string().optional(),
        type: z.string().optional(),
        groupId: z.string().optional(),
    })
        .refine((v) => Boolean(v.widgetId || v.type), {
        message: "Provide either target.widgetId or target.type",
    }),
    inputs: z.record(z.any()),
});
export const HighlightSchema = z.object({
    id: z.string(),
    ts: z.number(),
    direction: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
    magnitude: z.number().min(0).max(1),
    title: z.string(),
    summary: z.string().optional(),
    kind: z.enum(["impact", "news", "relationship", "theme"]),
    sourceIds: z.array(z.string()).optional(),
    path: z.array(z.string()).optional(),
    relatedSymbols: z.array(z.string()).optional(),
    link: z.string().url().optional(),
});
export const HighlightInsightSchema = z.object({
    highlightId: z.string(),
    summary: z.string(),
    impactScore: z.number().min(0).max(1),
    confidence: z.enum(["low", "medium", "high"]),
    affectedAreas: z.array(z.string()).optional(),
    affectedEntities: z
        .array(z.object({
        name: z.string(),
        ticker: z.string().optional(),
        role: z
            .enum([
            "supplier",
            "customer",
            "competitor",
            "partner",
            "regulator",
            "market",
            "investor",
            "other",
        ])
            .optional(),
    }))
        .optional(),
    recommendedActions: z.array(z.string()).optional(),
    catalysts: z.array(z.string()).optional(),
    risks: z.array(z.string()).optional(),
    supportingData: z
        .array(z.object({
        label: z.string(),
        value: z.string(),
    }))
        .optional(),
    horizon: z.enum(["immediate", "short", "medium", "long"]).optional(),
    generatedAt: z.string(),
    modelVersion: z.string(),
});
export const CalendarEventSchema = z.object({
    id: z.string(),
    ts: z.number(),
    type: z.enum(["earnings", "filing", "dividend", "split", "event"]),
    label: z.string(),
    link: z.string().url().optional(),
    metadata: z
        .object({
        // Common fields
        symbol: z.string().optional(),
        profileId: z.string().optional(),
        // Earnings specific
        consensusEPS: z.number().optional(),
        actualEPS: z.number().optional(),
        estimatedRevenue: z.number().optional(),
        actualRevenue: z.number().optional(),
        fiscalPeriod: z.string().optional(),
        fiscalYear: z.number().optional(),
        reportTime: z
            .enum(["pre-market", "post-market", "during-market"])
            .optional(),
        // Dividend specific
        amount: z.number().optional(),
        currency: z.string().optional(),
        paymentDate: z.string().optional(),
        recordDate: z.string().optional(),
        declarationDate: z.string().optional(),
        yield: z.number().optional(),
        frequency: z.string().optional(),
        // Split specific
        ratio: z.string().optional(),
        splitFrom: z.number().optional(),
        splitTo: z.number().optional(),
        // Filing specific
        formType: z.string().optional(),
        filingUrl: z.string().optional(),
        acceptanceDateTime: z.string().optional(),
        accessionNumber: z.string().optional(),
        // Macro/Economic event specific
        importance: z.enum(["low", "medium", "high"]).optional(),
        country: z.string().optional(),
        previous: z.number().optional(),
        forecast: z.number().optional(),
        actual: z.number().optional(),
        unit: z.string().optional(),
        source: z.string().optional(),
    })
        .optional(),
});
// Second-order domain summary (shared contract)
// moved earlier
export const DomainSummarySchema = z.object({
    domain: SecondOrderDomainKeySchema,
    headline: z.string(),
    sentimentScore: z.number().min(-1).max(1),
    sentimentDelta: z.number().min(-1).max(1),
    bulletPoints: z.array(z.string()).min(1),
    topEntities: z
        .array(z.object({
        name: z.string(),
        ticker: z.string().optional(),
        importance: z.enum(["critical", "major", "minor"]).optional(),
    }))
        .optional(),
    lastUpdated: z.string().optional(),
    windowStart: z.string().optional(),
    windowEnd: z.string().optional(),
});
// --- Akem Intelligence (Artifacts + Reports) ---
export const IntelligenceArtifactSchema = z.object({
    id: z.string(),
    instrumentId: z.string(),
    listingId: z.string().optional(),
    profileId: z.string().optional(),
    domain: SecondOrderDomainKeySchema,
    ts: z.number(),
    title: z.string(),
    direction: z.enum(["positive", "neutral", "negative"]),
    impactScore: z.number().min(0).max(1),
    severity: z.enum(["low", "normal", "elevated", "high"]),
    confidence: z.enum(["low", "medium", "high"]).optional().default("medium"),
    primarySymbol: z.string().optional(),
    sparkline: z.array(z.number()).min(5).max(100).optional(),
    significance: z.number().min(0).max(10).optional(),
    relatedSymbols: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    summary: z.string().optional(),
    body: z.string().optional(),
    sources: z
        .array(z.object({
        title: z.string(),
        url: z.string().optional(),
        source: z.string().optional(),
        publishedAt: z.string().optional(),
    }))
        .min(1),
    reportCacheKey: z.string().optional(),
});
export const IntelligenceReportSchema = z.object({
    artifactId: z.string(),
    objective: z.object({
        positive: z.array(z.string()),
        neutral: z.array(z.string()),
        negative: z.array(z.string()),
    }),
    decision: z.object({
        picked: z.enum(["positive", "neutral", "negative"]),
        rationale: z.string(),
    }),
    summary: z.string(),
    likelyOutcome: z.string(),
    // GPT-5 strict structured outputs require the `required` list to include
    // every key in `properties`. To preserve optional semantics while satisfying
    // strict validation, keep the property required but allow null.
    implications: z
        .object({
        stock: z.string().nullable(),
        timeframe: z.enum(["immediate", "short", "medium", "long"]).nullable(),
    })
        .nullable(),
    historicalEvidence: z.array(z.object({
        example: z.string(),
        // nullable to satisfy strict schema while allowing absence
        period: z.string().nullable(),
        description: z.string(),
        relevance: z.string(),
    })),
    sources: z
        .array(z.object({
        title: z.string(),
        // nullable to satisfy strict schema while allowing absence
        url: z.string().nullable(),
        source: z.string().nullable(),
        publishedAt: z.string().nullable(),
    }))
        .min(1),
    generatedAt: z.string(),
    modelVersion: z.string(),
});
// Steward News Queue schemas
export const NewsQueueItemSchema = z.object({
    eventId: z.string().min(1),
    ts: z.string().min(1),
    url: z.string().min(1),
    title: z.string().min(1),
    excerpt: z.string().optional(),
    source: z.string().optional(),
    domain: z.string().optional(),
    createdAt: z.string().min(1),
});
export const NewsQueueDetailSchema = NewsQueueItemSchema.extend({
    targetedInstruments: z.array(z.string().min(1)),
    storylineInstruments: z.array(z.string().min(1)),
});
export const TriggerStorylineRequestSchema = z.object({
    instrumentId: z.string().min(1),
});
